import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Equipment } from '@/types/equipment'
import { equipmentApi } from '@/api/equipmentApi'

export const useEquipmentStore = defineStore('equipment', () => {
  const equipment = ref<Equipment[]>([])
  const currentEquipment = ref<Equipment | null>(null)
  const nameCache = ref<Record<number, string>>({})
  const loading = ref(false)
  const error = ref<string | null>(null)

  const fetchEquipment = async () => {
    console.log('[equipmentStore] fetchEquipment called')
    loading.value = true
    error.value = null
    try {
      const res = await equipmentApi.getAll()
      // normalize response: support both array and paginated { results, count }
      if (Array.isArray(res)) {
        equipment.value = res
      } else if (res && typeof res === 'object') {
        const r = res as unknown as { results?: Equipment[] }
        if (Array.isArray(r.results)) {
          equipment.value = r.results
        } else {
          equipment.value = []
        }
      } else {
        equipment.value = []
      }
      // populate name cache
      if (Array.isArray(equipment.value)) {
        equipment.value.forEach((e) => {
          if (e && typeof e.id === 'number') nameCache.value[e.id] = e.name
        })
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch equipment'
    } finally {
      loading.value = false
    }
  }

  const fetchEquipmentById = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      currentEquipment.value = await equipmentApi.getById(id)
      if (currentEquipment.value && typeof currentEquipment.value.id === 'number') {
        nameCache.value[currentEquipment.value.id] = currentEquipment.value.name
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch equipment'
    } finally {
      loading.value = false
    }
  }

  async function fetchNameIfMissing(id: number) {
    if (typeof id !== 'number' || id <= 0) return undefined
    if (nameCache.value[id]) return nameCache.value[id]
    try {
      const res = await equipmentApi.getById(id)
      if (res && typeof (res as unknown as { id?: unknown }).id === 'number') {
        const r = res as unknown as Equipment
        nameCache.value[r.id] = r.name
        return r.name
      }
    } catch {
      // ignore
    }
    nameCache.value[id] = 'Unknown'
    return 'Unknown'
  }

  function getName(id: number) {
    if (!id) return 'Unknown'
    if (nameCache.value[id]) return nameCache.value[id]
    const item = equipment.value.find((e) => e.id === id)
    if (item) return item.name
    return 'Unknown'
  }

  const createEquipment = async (data: Partial<Equipment>) => {
    loading.value = true
    error.value = null
    try {
      const result = await equipmentApi.create(data)
      if (result.success && result.equipment) {
        // return created equipment; caller should refetch list if needed
        return result.equipment
      } else {
        throw new Error(result.message || 'Failed to create equipment')
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create equipment'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateEquipment = async (id: number, data: Partial<Equipment>) => {
    loading.value = true
    error.value = null
    try {
      const res = await equipmentApi.update(id, data)
      if (res && typeof res === 'object') {
        const r = res as unknown as { success?: boolean; equipment?: Equipment; message?: string }
        if (r.success && r.equipment) {
          const updatedEquipment = r.equipment as Equipment
          const list = Array.isArray(equipment.value) ? equipment.value : []
          const index = list.findIndex((eq) => eq.id === id)
          if (index !== -1) {
            const newList = [...list]
            newList[index] = updatedEquipment
            equipment.value = newList
          } else {
            equipment.value = [...list, updatedEquipment]
          }
          return updatedEquipment
        }
      } else {
        const rr = res as unknown as { message?: string }
        const msg = (res && rr.message) || 'Failed to update equipment'
        error.value = msg
        throw new Error(msg)
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update equipment'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteEquipment = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      await equipmentApi.delete(id)
      if (Array.isArray(equipment.value)) {
        equipment.value = equipment.value.filter((eq) => eq.id !== id)
      } else {
        equipment.value = []
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete equipment'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    equipment,
    currentEquipment,
    nameCache,
    loading,
    error,
    fetchEquipment,
    fetchEquipmentById,
    fetchNameIfMissing,
    getName,
    createEquipment,
    updateEquipment,
    deleteEquipment,
  }
})
